<template>
  <action-message :on="showToast" :error="true" :message="toastMessage" />
  <TransitionRoot as="template" :show="isOpen">
    <Dialog as="div" class="fixed inset-0 z-10 overflow-y-auto" @close="closeModal">
      <div
        class="
          flex
          items-center
          justify-center
          min-h-screen
          px-4
          pt-4
          pb-20
          text-center
          sm:block
          sm:p-0
        "
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="
              inline-block
              overflow-hidden
              text-left
              align-bottom
              transition-all
              transform
              bg-white
              rounded-lg
              shadow-xl
              sm:my-8
              sm:align-middle
              sm:max-w-lg
              sm:w-full
              p-6
            "
          >
            <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900">
              Select device
            </DialogTitle>
            <div class="mt-4">
              <label for="options" class="block text-sm font-medium text-gray-700">Options</label>
              <select
                id="options"
                v-model="selectedDeviceData"
                :disabled="options.length === 0"
                class="
                  mt-1
                  block
                  w-full
                  pl-3
                  pr-10
                  py-2
                  text-base
                  border-gray-300
                  focus:outline-none
                  focus:ring-indigo-500
                  focus:border-indigo-500
                  sm:text-sm
                  rounded-md
                "
              >
                <option v-for="option in options" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>

            <div class="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                class="
                  inline-flex
                  justify-center
                  w-full
                  rounded-md
                  border border-transparent
                  shadow-sm
                  px-4
                  py-2
                  bg-indigo-600
                  text-base
                  font-medium
                  text-white
                  hover:bg-indigo-700
                  focus:outline-none
                  focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                  sm:ml-3
                  sm:w-auto
                  sm:text-sm
                "
                @click="saveSelection"
              >
                Save
              </button>
              <button
                type="button"
                class="
                  mt-3
                  w-full
                  inline-flex
                  justify-center
                  rounded-md
                  border border-gray-300
                  shadow-sm
                  px-4
                  py-2
                  bg-white
                  text-base
                  font-medium
                  text-gray-700
                  hover:bg-gray-50
                  focus:outline-none
                  focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                  sm:mt-0
                  sm:w-auto
                  sm:text-sm
                "
                @click="closeModal"
              >
                Cancel
              </button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { ref, watch, computed } from 'vue';
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import useDeviceAndSessionSetupStore from '@/Stores/useDeviceAndSessionSetupStore';
import {
  DEVICE_SLOT_ACTIONS,
  DERMATOSCOPE_LABELS,
  OTOSCOPE_LABELS,
  MONITOR_LABELS,
  STETHOSCOPE_LABELS,
  EXTERNAL_CONNECTED_VIDEO_DEVICES,
  EXTERNAL_CONNECTED_AUDIO_DEVICES,
} from '@/Utils/vice-constants';
import ActionMessage from '@/Jetstream/ActionMessage.vue';
import useEventsBus from '@/Services/EventBus.js';

export default {
  components: {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    ActionMessage,
  },
  setup() {
    const isOpen = ref(false);

    const devices = ref([]);

    const selectedDeviceData = ref('');

    const useCaseId = ref(null);
    const deviceSlotId = ref(null);
    const deviceKind = ref(null);
    const deviceTypeLabel = ref(null);

    const showToast = ref(false);
    const toastMessage = ref('Device adding error');

    const deviceAndSessionSetupStore = useDeviceAndSessionSetupStore();

    const { bus, emit } = useEventsBus();

    // watching for Pinia srotage iniitalization to get devices list according to
    // slot device kind
    watch(
      () => deviceAndSessionSetupStore.getConnectedDevices(deviceKind.value),
      (devicesList) => {
        devices.value = devicesList;
      },
    );

    // available devices list for 'select' depends on device name
    const options = computed(() => {
      const optionsArray = [];
      let filteredDevices;

      switch (deviceTypeLabel.value) {
        case EXTERNAL_CONNECTED_VIDEO_DEVICES.OTOSCOPE:
          filteredDevices = filterExternalConnectedDevices(OTOSCOPE_LABELS);
          break;
        case EXTERNAL_CONNECTED_VIDEO_DEVICES.DERMATOSCOPE:
          filteredDevices = filterExternalConnectedDevices(DERMATOSCOPE_LABELS);
          break;
        case EXTERNAL_CONNECTED_VIDEO_DEVICES.MONITOR:
          filteredDevices = filterExternalConnectedDevices(MONITOR_LABELS);
          break;
        case EXTERNAL_CONNECTED_AUDIO_DEVICES.STETHOSCOPE:
          filteredDevices = filterExternalConnectedDevices(STETHOSCOPE_LABELS);
          break;
        default:
          filteredDevices = devices.value;
          break;
      }

      filteredDevices.forEach((device) => {
        optionsArray.push({
          value: device,
          label: device.label,
        });
      });

      return optionsArray;
    });

    const filterExternalConnectedDevices = (externalVideoDeviceLabels) =>
      devices.value.filter((device) =>
        externalVideoDeviceLabels.some((keyword) => device.label === keyword),
      );

    const closeModal = () => {
      isOpen.value = false;
    };

    // Handle "Save" button
    const saveSelection = () => {
      axios
        .post(route('clinician.set-device'), {
          use_case_id: useCaseId.value,
          use_case_slot_id: deviceSlotId.value,
          device_data: selectedDeviceData.value,
        })
        .then(() => {
          // emit event when clinician saved device in slot
          emit('deviceAddedToDeviceTypeSlot', {
            deviceSlotId: deviceSlotId.value,
            deviceLabel: selectedDeviceData.value.label,
            deviceKind: selectedDeviceData.value.kind,
            deviceId: selectedDeviceData.value.deviceId,
          });

          // add to pinia
          deviceAndSessionSetupStore.manageClinicianDeviceSlot(
            deviceSlotId.value,
            DEVICE_SLOT_ACTIONS.ADD_DEVICE,
            selectedDeviceData.value,
          );

          closeModal();
          selectedDeviceData.value = '';
        })
        .catch((e) => {
          console.log(e);
          showToast.value = true;
        });
    };

    // listen to event when clinician trying to add device
    watch(
      () => bus.value.get('showSetDeviceModal'),
      (deviceSlotPropertiesArgs) => {
        isOpen.value = true;
        useCaseId.value = deviceSlotPropertiesArgs[0].useCaseId;
        deviceSlotId.value = deviceSlotPropertiesArgs[0].deviceSlotId;
        deviceKind.value = deviceSlotPropertiesArgs[0].deviceKind;
        deviceTypeLabel.value = deviceSlotPropertiesArgs[0].deviceTypeLabel;
      },
    );

    return {
      isOpen,
      selectedDeviceData,
      options,
      closeModal,
      saveSelection,
      showToast,
      toastMessage,
    };
  },
};
</script>

<style scoped>
/* Add any custom styles here if needed */
</style>
